import type { BoxConfigInput } from "../types.js";

/**
 * Núcleo PURO do fluxo de planilha do grupo de embalagem (Design.md §6.2 +
 * entrega esperada). Gera o modelo de linhas do template (download), faz parse
 * da matriz preenchida (upload) e VALIDA mostrando exatamente onde corrigir
 * (por SKU / por caixa). Sem exceljs/DOM — testável em node. A camada de I/O
 * xlsx (exceljs) vive em box-content-workbook.ts e só faz ler/escrever a matriz.
 *
 * Regra-chave (a "célula vermelha" da Amazon): para cada SKU, a soma das
 * quantidades por caixa precisa ser igual à Quantidade prevista do envio.
 */

export type Cell = string | number | null | undefined;
export type Matrix = ReadonlyArray<ReadonlyArray<Cell>>;

/** SKU do grupo, montado a partir dos itens do plano + prep details. */
export interface BoxContentSku {
  sku: string;
  title: string;
  asin: string;
  fnsku: string;
  /** Imagem do produto (join com o snapshot de inventário). Só para exibição. */
  imageUrl?: string;
  prepType: string;
  preparedBy: string;
  labeledBy: string;
  expectedQuantity: number;
}

export interface BoxContentTemplate {
  packingGroupId: string;
  boxCount: number;
  skus: readonly BoxContentSku[];
}

export interface ParsedSku {
  sku: string;
  expectedQuantity: number;
  quantities: number[];
}

export interface ParsedBox {
  weightKg: number;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
}

export interface ParsedBoxContent {
  packingGroupId: string;
  boxCount: number;
  skus: ParsedSku[];
  boxes: ParsedBox[];
}

export type ParseResult =
  | { ok: true; value: ParsedBoxContent }
  | { ok: false; error: string };

export interface BoxContentError {
  message: string;
  sku?: string;
  boxNumber?: number;
}

export interface BoxContentValidation {
  errors: BoxContentError[];
  boxConfig: BoxConfigInput | null;
}

// ───────────────────────────── Layout (write + parse) ───────────────────────
// 8 colunas fixas (índices 0–7) e colunas de caixa a partir do índice 8.
const FIXED_HEADERS = [
  "Código SKU",
  "Título",
  "ASIN",
  "FNSKU",
  "Tipo de preparação",
  "Preparado por",
  "Etiquetado por",
  "Quantidade prevista"
] as const;
const FIRST_BOX_COL = 8;
const EXPECTED_QTY_COL = 7;

const LABEL_GROUP = "Grupo de embalagem:";
const LABEL_BOX_COUNT = "Contagem total de caixas:";
const LABEL_HEADER = "Código SKU";
const LABEL_WEIGHT = "Peso da caixa (kg)";
const LABEL_WIDTH = "Largura da caixa (cm)";
const LABEL_LENGTH = "Comprimento da caixa (cm)";
const LABEL_HEIGHT = "Altura da caixa (cm)";

export const INFO_SHEET_NAME = "Informações sobre embalagem";

function s(cell: Cell): string {
  return cell === null || cell === undefined ? "" : String(cell).trim();
}

function toInt(cell: Cell): number {
  const raw = s(cell);
  if (raw === "") {
    return 0;
  }
  const n = typeof cell === "number" ? cell : Number(raw);
  return Number.isFinite(n) ? Math.trunc(n) : Number.NaN;
}

function toNum(cell: Cell): number {
  const raw = s(cell);
  if (raw === "") {
    return 0;
  }
  const n = typeof cell === "number" ? cell : Number(raw);
  return Number.isFinite(n) ? n : Number.NaN;
}

function emptyCells(count: number): Cell[] {
  return Array.from({ length: count }, () => null);
}

/** Linhas da aba "Informações" do template (download), com caixas em branco. */
export function buildTemplateRows(template: BoxContentTemplate): Cell[][] {
  const { packingGroupId, boxCount, skus } = template;
  const boxHeaders: Cell[] = Array.from(
    { length: boxCount },
    (_unused, i) => `Caixa ${i + 1}`
  );

  const rows: Cell[][] = [];
  rows.push([
    "Preencha as quantidades por caixa e o peso/dimensões de cada caixa. Não altere os cabeçalhos."
  ]);
  rows.push([LABEL_GROUP, packingGroupId]);
  rows.push([LABEL_BOX_COUNT, boxCount]);
  rows.push([]);
  rows.push([...FIXED_HEADERS, ...boxHeaders]);
  for (const sku of skus) {
    rows.push([
      sku.sku,
      sku.title,
      sku.asin,
      sku.fnsku,
      sku.prepType,
      sku.preparedBy,
      sku.labeledBy,
      sku.expectedQuantity,
      ...emptyCells(boxCount)
    ]);
  }
  rows.push([]);
  for (const label of [LABEL_WEIGHT, LABEL_WIDTH, LABEL_LENGTH, LABEL_HEIGHT]) {
    rows.push([label, ...emptyCells(FIRST_BOX_COL - 1), ...emptyCells(boxCount)]);
  }
  return rows;
}

function findRow(rows: Matrix, label: string): number {
  return rows.findIndex((row) => s(row[0]) === label);
}

function boxCells(row: ReadonlyArray<Cell> | undefined, boxCount: number): Cell[] {
  const out: Cell[] = [];
  for (let b = 0; b < boxCount; b += 1) {
    out.push(row?.[FIRST_BOX_COL + b] ?? null);
  }
  return out;
}

/** Parseia a matriz da aba "Informações" preenchida pelo usuário. */
export function parseMatrix(rows: Matrix): ParseResult {
  const groupIdx = findRow(rows, LABEL_GROUP);
  const countIdx = findRow(rows, LABEL_BOX_COUNT);
  const headerIdx = findRow(rows, LABEL_HEADER);
  const weightIdx = findRow(rows, LABEL_WEIGHT);
  const widthIdx = findRow(rows, LABEL_WIDTH);
  const lengthIdx = findRow(rows, LABEL_LENGTH);
  const heightIdx = findRow(rows, LABEL_HEIGHT);

  if (
    groupIdx < 0 ||
    countIdx < 0 ||
    headerIdx < 0 ||
    weightIdx < 0 ||
    widthIdx < 0 ||
    lengthIdx < 0 ||
    heightIdx < 0
  ) {
    return {
      ok: false,
      error:
        "Planilha em formato inválido. Baixe o template novamente e preencha sem remover linhas/cabeçalhos."
    };
  }

  const packingGroupId = s(rows[groupIdx]?.[1]);
  const boxCount = toInt(rows[countIdx]?.[1]);
  if (packingGroupId === "") {
    return { ok: false, error: "Grupo de embalagem ausente na planilha." };
  }
  if (!Number.isFinite(boxCount) || boxCount < 1) {
    return { ok: false, error: "Contagem total de caixas inválida." };
  }

  const skus: ParsedSku[] = [];
  for (let i = headerIdx + 1; i < weightIdx; i += 1) {
    const row = rows[i];
    const sku = s(row?.[0]);
    if (sku === "") {
      continue;
    }
    skus.push({
      sku,
      expectedQuantity: toInt(row?.[EXPECTED_QTY_COL]),
      quantities: boxCells(row, boxCount).map(toInt)
    });
  }

  const weightCells = boxCells(rows[weightIdx], boxCount).map(toNum);
  const widthCells = boxCells(rows[widthIdx], boxCount).map(toNum);
  const lengthCells = boxCells(rows[lengthIdx], boxCount).map(toNum);
  const heightCells = boxCells(rows[heightIdx], boxCount).map(toNum);

  const boxes: ParsedBox[] = Array.from({ length: boxCount }, (_unused, b) => ({
    weightKg: weightCells[b] ?? Number.NaN,
    widthCm: widthCells[b] ?? Number.NaN,
    lengthCm: lengthCells[b] ?? Number.NaN,
    heightCm: heightCells[b] ?? Number.NaN
  }));

  return { ok: true, value: { packingGroupId, boxCount, skus, boxes } };
}

function dimsValid(box: ParsedBox): boolean {
  return (
    Number.isFinite(box.weightKg) &&
    box.weightKg > 0 &&
    Number.isFinite(box.lengthCm) &&
    box.lengthCm > 0 &&
    Number.isFinite(box.widthCm) &&
    box.widthCm > 0 &&
    Number.isFinite(box.heightCm) &&
    box.heightCm > 0
  );
}

function anyDim(box: ParsedBox): boolean {
  return (
    (Number.isFinite(box.weightKg) && box.weightKg > 0) ||
    (Number.isFinite(box.lengthCm) && box.lengthCm > 0) ||
    (Number.isFinite(box.widthCm) && box.widthCm > 0) ||
    (Number.isFinite(box.heightCm) && box.heightCm > 0)
  );
}

/**
 * Dados AUTORITATIVOS (do plano, não da planilha): a planilha tem células
 * editáveis (quantidade prevista, grupo) que o usuário pode adulterar, então a
 * validação cruza sempre com estes valores, nunca com o que veio do arquivo.
 */
export interface BoxContentAuthoritative {
  packingGroupId: string;
  expectedBySku: ReadonlyMap<string, number>;
}

/** Valida o conteúdo e, se OK, monta o BoxConfigInput para o backend. */
export function validateBoxContent(
  parsed: ParsedBoxContent,
  authoritative: BoxContentAuthoritative
): BoxContentValidation {
  const errors: BoxContentError[] = [];
  const { boxCount, skus, boxes } = parsed;
  // packingGroupId vem do plano, NUNCA da célula editável da planilha.
  const packingGroupId = authoritative.packingGroupId;

  if (parsed.packingGroupId !== authoritative.packingGroupId) {
    errors.push({
      message:
        "A planilha não corresponde a este grupo de embalagem. Baixe o template novamente."
    });
  }

  if (skus.length === 0) {
    errors.push({ message: "Nenhum SKU encontrado no grupo de embalagem." });
  }

  // Garante que todo SKU esperado do plano está presente na planilha.
  const parsedSkuSet = new Set(skus.map((s) => s.sku));
  for (const expectedSku of authoritative.expectedBySku.keys()) {
    if (!parsedSkuSet.has(expectedSku)) {
      errors.push({
        sku: expectedSku,
        message: `SKU ${expectedSku}: ausente na planilha (não remova linhas de SKU).`
      });
    }
  }

  // Regra da soma por SKU + quantidades válidas, contra a quantidade prevista
  // AUTORITATIVA do plano.
  for (const sku of skus) {
    const expected = authoritative.expectedBySku.get(sku.sku);
    if (expected === undefined) {
      errors.push({
        sku: sku.sku,
        message: `SKU ${sku.sku}: não pertence a este grupo de embalagem.`
      });
      continue;
    }
    let hasInvalid = false;
    for (let b = 0; b < boxCount; b += 1) {
      const q = sku.quantities[b] ?? Number.NaN;
      if (Number.isNaN(q)) {
        hasInvalid = true;
        errors.push({
          sku: sku.sku,
          boxNumber: b + 1,
          message: `SKU ${sku.sku}, Caixa ${b + 1}: quantidade inválida.`
        });
      } else if (q < 0) {
        hasInvalid = true;
        errors.push({
          sku: sku.sku,
          boxNumber: b + 1,
          message: `SKU ${sku.sku}, Caixa ${b + 1}: quantidade não pode ser negativa.`
        });
      }
    }
    if (!hasInvalid) {
      const sum = sku.quantities.reduce((a, q) => a + (q ?? 0), 0);
      if (sum !== expected) {
        errors.push({
          sku: sku.sku,
          message: `SKU ${sku.sku}: soma das caixas (${sum}) ≠ quantidade prevista (${expected}).`
        });
      }
    }
  }

  // Peso/dimensões por caixa preenchida.
  for (let b = 0; b < boxCount; b += 1) {
    const box = boxes[b];
    if (box === undefined) {
      continue;
    }
    const itemsInBox = skus.reduce((a, sku) => {
      const q = sku.quantities[b] ?? Number.NaN;
      return a + (Number.isNaN(q) || q < 0 ? 0 : q);
    }, 0);
    if (itemsInBox > 0) {
      if (!dimsValid(box)) {
        errors.push({
          boxNumber: b + 1,
          message: `Caixa ${b + 1}: informe peso (kg) e dimensões (cm) válidos.`
        });
      }
    } else if (anyDim(box)) {
      errors.push({
        boxNumber: b + 1,
        message: `Caixa ${b + 1}: tem peso/dimensões mas nenhuma unidade.`
      });
    }
  }

  if (errors.length > 0) {
    return { errors, boxConfig: null };
  }

  // Monta o BoxConfigInput só com caixas que têm unidades (Amazon exige itens).
  const boxesOut = [];
  for (let b = 0; b < boxCount; b += 1) {
    const box = boxes[b];
    if (box === undefined) {
      continue;
    }
    const items = skus
      .map((sku) => ({ sku: sku.sku, quantity: sku.quantities[b] ?? 0 }))
      .filter((item) => item.quantity > 0);
    if (items.length === 0) {
      continue;
    }
    boxesOut.push({
      boxNumber: b + 1,
      lengthCm: box.lengthCm,
      widthCm: box.widthCm,
      heightCm: box.heightCm,
      weightKg: box.weightKg,
      items
    });
  }

  if (boxesOut.length === 0) {
    return {
      errors: [{ message: "Preencha ao menos uma caixa com unidades." }],
      boxConfig: null
    };
  }

  return { errors: [], boxConfig: { packingGroupId, boxes: boxesOut } };
}

const FIELD_LABELS: Record<string, string> = {
  weightKg: "peso (kg)",
  lengthCm: "comprimento (cm)",
  widthCm: "largura (cm)",
  heightCm: "altura (cm)",
  boxNumber: "número da caixa",
  quantity: "quantidade",
  sku: "SKU"
};

/**
 * Traduz os erros de validação Zod do backend (ex.: "boxes.0.weightKg") em
 * erros localizados por caixa, usando os boxNumber reais do BoxConfigInput
 * enviado (a ordem do array enviado pode pular caixas vazias).
 */
export function mapBackendFieldErrors(
  fields: Record<string, string>,
  boxConfig: BoxConfigInput | null
): BoxContentError[] {
  const out: BoxContentError[] = [];
  for (const [path, message] of Object.entries(fields)) {
    const boxMatch = /^boxes\.(\d+)\.(\w+)/.exec(path);
    if (boxMatch !== null) {
      const idx = Number(boxMatch[1]);
      const field = boxMatch[2] ?? "";
      const boxNumber = boxConfig?.boxes[idx]?.boxNumber ?? idx + 1;
      const label = FIELD_LABELS[field] ?? field;
      out.push({ boxNumber, message: `Caixa ${boxNumber}: ${label} — ${message}` });
    } else {
      out.push({ message: `${path}: ${message}` });
    }
  }
  return out;
}
